import React, { useRef, useState } from "react";
import axios from "../../api";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Divider,
  LinearProgress,
} from "@mui/material";
import { Download, UploadFile, CheckCircle } from "@mui/icons-material";

// Generic Excel bulk-import dialog.
// - Downloads a pre-formatted .xlsx template
// - Parses an uploaded .xlsx/.csv on the client
// - Previews rows and posts them as JSON to `endpoint`
function BulkUploadDialog({
  open,
  onClose,
  title,
  endpoint,
  columns, // [{ key, label, required }]
  sample, // example row object, or array of example rows
  templateName = "template",
  onImported,
}) {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const keys = columns.map((c) => c.key);

  const reset = () => {
    setRows([]);
    setFileName("");
    setError("");
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: the fill-in template (headers + example row(s))
    const sampleRows = Array.isArray(sample)
      ? sample
      : sample
        ? [sample]
        : [{}];
    const ws = XLSX.utils.json_to_sheet(sampleRows, { header: keys });
    ws["!cols"] = keys.map((k) => ({ wch: Math.max(14, k.length + 2) }));
    XLSX.utils.book_append_sheet(wb, ws, "Template");

    // Sheet 2: instructions / allowed values for each column
    const instructions = columns.map((c) => ({
      Field: c.key,
      Required: c.required ? "Yes" : "No",
      "Allowed values / Notes": c.note || "",
    }));
    const wsInfo = XLSX.utils.json_to_sheet(instructions, {
      header: ["Field", "Required", "Allowed values / Notes"],
    });
    wsInfo["!cols"] = [{ wch: 18 }, { wch: 10 }, { wch: 60 }];
    XLSX.utils.book_append_sheet(wb, wsInfo, "Instructions");

    XLSX.writeFile(wb, `${templateName}.xlsx`);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    setResult(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const parsed = XLSX.utils
        .sheet_to_json(ws, { defval: "" })
        .filter((r) => Object.values(r).some((v) => String(v).trim() !== ""));
      if (!parsed.length) {
        setError("The file has no data rows.");
        return;
      }
      setRows(parsed);
      setFileName(file.name);
    } catch (err) {
      console.error(err);
      setError("Could not read the file. Please upload a valid .xlsx or .csv.");
    }
  };

  const handleImport = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await axios.post(endpoint, rows);
      setResult(resp.data);
      if (onImported) onImported(resp.data);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  const downloadCredentials = (created) => {
    const header = "email,name,password";
    const lines = created.map(
      (c) => `${c.email},${(c.name || "").replace(/,/g, " ")},${c.password}`
    );
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "credentials.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const previewRows = rows.slice(0, 5);
  const failedList = result?.failed || result?.skipped || [];
  // `created` is a number for asset imports, an array of user objects for user
  // imports — only the latter carries generated passwords.
  const createdWithPw = Array.isArray(result?.created)
    ? result.created.filter((c) => c && c.password)
    : [];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Step 1: template */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            1. Download the template and fill it in
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
            {columns.map((c) => (
              <Chip
                key={c.key}
                size="small"
                label={c.required ? `${c.label} *` : c.label}
                color={c.required ? "primary" : "default"}
                variant={c.required ? "filled" : "outlined"}
              />
            ))}
          </Box>
          <Button startIcon={<Download />} onClick={downloadTemplate} variant="outlined" size="small">
            Download Template (.xlsx)
          </Button>
          <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
            Fields marked * are required. Extra columns are ignored.
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Step 2: upload */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            2. Upload the completed file
          </Typography>
          <Button startIcon={<UploadFile />} variant="contained" component="label" size="small">
            Choose File
            <input
              ref={fileRef}
              hidden
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFile}
            />
          </Button>
          {fileName && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <b>{fileName}</b> — {rows.length} row(s) detected
            </Typography>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Preview */}
        {rows.length > 0 && !result && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Preview (first {previewRows.length} of {rows.length})
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {keys.map((k) => (
                      <TableCell key={k}>{k}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map((r, i) => (
                    <TableRow key={i}>
                      {keys.map((k) => (
                        <TableCell key={k}>{String(r[k] ?? "")}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}

        {/* Result */}
        {result && (
          <Box>
            <Alert
              severity="success"
              icon={<CheckCircle fontSize="inherit" />}
              sx={{ mb: 2 }}
            >
              Import complete.{" "}
              {result.created !== undefined &&
                typeof result.created === "number" &&
                `${result.created} created. `}
              {result.createdCount !== undefined && `${result.createdCount} created. `}
              {result.invitedCount !== undefined &&
                `${result.invitedCount} activation email(s) sent. `}
              {result.updated !== undefined && `${result.updated} updated. `}
              {failedList.length > 0 && `${failedList.length} skipped/failed.`}
            </Alert>

            {result.emailFailedCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {result.emailFailedCount} user(s) were created but their activation
                email could not be sent. You can resend invitations from the Users
                list.
              </Alert>
            )}

            {createdWithPw.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Passwords are shown once. Download and share them securely.
                </Alert>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => downloadCredentials(createdWithPw)}
                >
                  Download Credentials (.csv)
                </Button>
              </Box>
            )}

            {failedList.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Skipped / failed rows
                </Typography>
                <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Line</TableCell>
                        <TableCell>Identifier</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {failedList.map((f, i) => (
                        <TableRow key={i}>
                          <TableCell>{f.line ?? "-"}</TableCell>
                          <TableCell>{f.email || "-"}</TableCell>
                          <TableCell>{f.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{result ? "Close" : "Cancel"}</Button>
        {!result && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={rows.length === 0 || loading}
          >
            Import {rows.length > 0 ? `${rows.length} row(s)` : ""}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default BulkUploadDialog;
